<script setup lang="ts">
import { computed, ref } from "vue";
import { Dialog } from "../../../../base-components/Headless";
import Button from "../../../../base-components/Button";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import { usePedidosStore } from "../../../../services/pedidos";
import { useMensajesStore } from "../../../../services/mensajes";
import { userUserStore } from "../../../../stores/user";
import { formatearFechaTimestampDDMMYYYY } from "../../../../utils/fecha";
import type { Pedido } from "@fixeduy/restaurantestypes";
import {
  ventaDevolucionTotalCrear,
  type ApiFixedVentaDevolucionTotalCrearRequest
} from "../../../../services/cloudFunctions/fixed/apiFixedVentaDevolucionTotalCrear";
import { printHTMLContent } from "../../../../utils/helper";

interface Props {
  pedido: Pedido;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const pedidosStore = usePedidosStore();
const mensajesStore = useMensajesStore();
const userStore = userUserStore();

const sendButtonRef = ref(null);
const cargando = ref(false);
const errorMsg = ref<string>("");

const tipoCfeLabel = computed(() =>
  props.pedido.cfeTipo === "101" ? "e-Ticket" : "e-Factura"
);

const cfeLabel = computed(
  () =>
    `${tipoCfeLabel.value} ${props.pedido.cfeSerie} ${props.pedido.cfeNumero}`
);

const confirmarAnulacion = async () => {
  if (cargando.value) return;
  cargando.value = true;
  errorMsg.value = "";

  try {
    const empresaRut = userStore.empresa?.rut;
    if (!empresaRut) {
      throw new Error("No hay RUT de la empresa configurado.");
    }

    const fechaPedido =
      props.pedido.fechaCierre !== 0
        ? props.pedido.fechaCierre
        : props.pedido.fechaCreacion;

    const payload: ApiFixedVentaDevolucionTotalCrearRequest = {
      apiKey: "JU4L6W22-0P8A-G5F3-PN3Z-438UNRWV9G5J",
      rutComercio: empresaRut.toString(),
      idCFE: props.pedido.cfeId,
      fecha: formatearFechaTimestampDDMMYYYY(fechaPedido, false),
      cotizacion: 1,
      ordenCompra: "",
      local: "",
      adenda: props.pedido.observaciones ?? ""
    };

    const respuesta = await ventaDevolucionTotalCrear(payload);

    const okPersist = await pedidosStore.updatePedido(props.pedido.id, {
      devuelto: true,
      cfeIdDevolucion: respuesta.idCFE,
      cfeNumeroDevolucion: respuesta.numero,
      cfeSerieDevolucion: respuesta.serie,
      cfeTipoDevolucion: respuesta.tipoCFE,
      ventaIdDevolucion: respuesta.idVenta
    });

    if (!okPersist) {
      mensajesStore.mostrarToast(
        "Anulación enviada",
        "Fixed respondió OK, pero no se pudo actualizar el pedido en la base de datos.",
        mensajesStore.mensajeType.error
      );
      emit("cerrar");
      return;
    }

    mensajesStore.mostrarToast(
      "Factura anulada",
      "La devolución total fue enviada a Fixed correctamente.",
      mensajesStore.mensajeType.exito
    );
    mensajesStore.mostrarToast(
      "Factura Fixed enviada",
      "Cargando impresion...",
      mensajesStore.mensajeType.exito
    );
    await printHTMLContent(respuesta.htmlTicket);
    emit("cerrar");
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    errorMsg.value = msg;
    if (msg.includes("ocumento ya afectado")) {
      errorMsg.value +=
        "\n\nDocumento ya devuelto en Fixed, no es posible generar la devolución.";
      const okPersist = await pedidosStore.updatePedido(props.pedido.id, {
        devuelto: true
      });
    }
    mensajesStore.mostrarToast(
      "Error al anular factura",
      msg,
      mensajesStore.mensajeType.error
    );
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <Dialog
    :open="true"
    @close="
      () => {
        emit('cerrar');
      }
    "
    :initialFocus="sendButtonRef"
    size="xl"
  >
    <Dialog.Panel
      class="overflow-auto flex flex-col max-w-[100vw] max-h-[85vh]"
    >
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Anular factura</h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="px-1 py-1">
          <p class="mt-1 text-sm text-slate-700">
            ¿Quiere anular la factura correspondiente?
          </p>
          <p class="mt-2 text-sm font-medium text-slate-900">
            {{ cfeLabel }}
          </p>
        </div>
        <pre>
          {{ props.pedido.devuelto }}
        </pre>

        <p
          v-if="errorMsg"
          class="mt-3 text-sm text-danger whitespace-pre-line"
          role="alert"
        >
          {{ errorMsg }}
        </p>
      </Dialog.Description>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="cargando" class="flex justify-center items-center">
          <LoadingIcon icon="oval" class="w-8 h-8" />
          <span class="ml-2 text-sm text-slate-600">Procesando...</span>
        </div>
        <div v-else class="flex text-right">
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1">
            <Button
              ref="sendButtonRef"
              variant="danger"
              class="mb-2 mr-1"
              @click="confirmarAnulacion"
              :disabled="props.pedido.devuelto"
            >
              Anular
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
